import { useEffect, useState } from 'react'
import { collection, getDocs, query, where, orderBy, updateDoc, doc, getDoc } from 'firebase/firestore'
import { db } from '../../services/firebase'
import { ArrowLeft, Loader, DollarSign, CheckCircle, XCircle, Calendar, User, CreditCard } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'

export default function Withdrawals() {
  const [withdrawals, setWithdrawals] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('pending') // pending, approved, rejected

  useEffect(() => {
    fetchWithdrawals()
  }, [])

  const fetchWithdrawals = async () => {
    setLoading(true)
    try {
      const q = query(collection(db, 'withdrawalRequests'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setWithdrawals(data)
    } catch (error) {
      console.error('Error fetching withdrawals:', error)
      toast.error('Error al cargar retiros')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (withdrawal) => {
    if (!confirm(`¿Aprobar retiro de $${withdrawal.amount.toFixed(2)} para ${withdrawal.userName}?`)) return

    try {
      // Update withdrawal status
      await updateDoc(doc(db, 'withdrawalRequests', withdrawal.id), {
        status: 'approved',
        approvedAt: new Date(),
      })

      // Update transaction status
      const transactionsRef = collection(db, 'transactions')
      const q = query(
        transactionsRef,
        where('userId', '==', withdrawal.userId),
        where('type', '==', 'withdrawal'),
        where('status', '==', 'pending'),
        where('amount', '==', withdrawal.amount)
      )
      const transactionsSnapshot = await getDocs(q)
      const pendingTransaction = transactionsSnapshot.docs[0]
      
      if (pendingTransaction) {
        await updateDoc(doc(db, 'transactions', pendingTransaction.id), {
          status: 'approved',
        })
      }

      toast.success('Retiro aprobado correctamente')
      fetchWithdrawals()
    } catch (error) {
      console.error('Error approving withdrawal:', error)
      toast.error('Error al aprobar retiro')
    }
  }

  const handleReject = async (withdrawal) => {
    const reason = prompt('Motivo del rechazo:')
    if (!reason) return

    try {
      // Update withdrawal status
      await updateDoc(doc(db, 'withdrawalRequests', withdrawal.id), {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectionReason: reason,
      })

      // Return money to user's wallet
      const userDoc = await getDoc(doc(db, 'users', withdrawal.userId))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        const newWinnings = (userData.wallet?.winnings || 0) + withdrawal.amount
        const newTotal = (userData.wallet?.total || 0) + withdrawal.amount

        await updateDoc(doc(db, 'users', withdrawal.userId), {
          'wallet.winnings': newWinnings,
          'wallet.total': newTotal,
        })
      }

      // Update transaction status
      const transactionsRef = collection(db, 'transactions')
      const q = query(
        transactionsRef,
        where('userId', '==', withdrawal.userId),
        where('type', '==', 'withdrawal'),
        where('status', '==', 'pending'),
        where('amount', '==', withdrawal.amount)
      )
      const transactionsSnapshot = await getDocs(q)
      const pendingTransaction = transactionsSnapshot.docs[0]
      
      if (pendingTransaction) {
        await updateDoc(doc(db, 'transactions', pendingTransaction.id), {
          status: 'rejected',
          rejectionReason: reason,
        })
      }

      toast.success('Retiro rechazado y saldo devuelto')
      fetchWithdrawals()
    } catch (error) {
      console.error('Error rejecting withdrawal:', error)
      toast.error('Error al rechazar retiro')
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredWithdrawals = withdrawals.filter(w => {
    if (activeTab === 'pending') return w.status === 'pending'
    if (activeTab === 'approved') return w.status === 'approved'
    if (activeTab === 'rejected') return w.status === 'rejected'
    return true
  })

  const stats = {
    pending: withdrawals.filter(w => w.status === 'pending').length,
    approved: withdrawals.filter(w => w.status === 'approved').length,
    rejected: withdrawals.filter(w => w.status === 'rejected').length,
    totalPending: withdrawals
      .filter(w => w.status === 'pending')
      .reduce((sum, w) => sum + w.amount, 0),
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F8F8F8] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/operator"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F8F8F8] mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Volver al Panel
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#D4AF37] mb-2 flex items-center gap-3">
                <DollarSign size={32} />
                Gestión de Retiros
              </h1>
              <p className="text-[#BDBDBD]">
                Administra las solicitudes de retiro de los usuarios
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
            <p className="text-sm text-[#BDBDBD]">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
          </div>
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
            <p className="text-sm text-[#BDBDBD]">Monto Pendiente</p>
            <p className="text-2xl font-bold text-yellow-500">${stats.totalPending.toFixed(2)}</p>
          </div>
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
            <p className="text-sm text-[#BDBDBD]">Aprobados</p>
            <p className="text-2xl font-bold text-[#22C55E]">{stats.approved}</p>
          </div>
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
            <p className="text-sm text-[#BDBDBD]">Rechazados</p>
            <p className="text-2xl font-bold text-[#EF4444]">{stats.rejected}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg mb-6">
          <div className="flex border-b border-[#0F3D2E]">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'bg-[#0F3D2E] text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-[#BDBDBD] hover:text-[#F8F8F8]'
              }`}
            >
              Pendientes ({stats.pending})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'bg-[#0F3D2E] text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-[#BDBDBD] hover:text-[#F8F8F8]'
              }`}
            >
              Aprobados ({stats.approved})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'bg-[#0F3D2E] text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-[#BDBDBD] hover:text-[#F8F8F8]'
              }`}
            >
              Rechazados ({stats.rejected})
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="animate-spin text-[#D4AF37]" size={48} />
          </div>
        ) : filteredWithdrawals.length === 0 ? (
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-12 text-center">
            <DollarSign className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
            <h3 className="text-xl font-semibold text-[#F8F8F8] mb-2">
              No hay retiros {activeTab === 'pending' ? 'pendientes' : activeTab === 'approved' ? 'aprobados' : 'rechazados'}
            </h3>
            <p className="text-[#BDBDBD]">
              Los retiros aparecerán aquí cuando haya solicitudes
            </p>
          </div>
        ) : (
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-[#F8F8F8]">
                <thead>
                  <tr className="bg-[#0F3D2E]">
                    <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Monto</th>
                    <th className="px-4 py-3 text-left font-semibold">CLABE</th>
                    <th className="px-4 py-3 text-left font-semibold">Beneficiario</th>
                    <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                    {activeTab === 'pending' && (
                      <th className="px-4 py-3 text-left font-semibold">Acciones</th>
                    )}
                    {activeTab === 'rejected' && (
                      <th className="px-4 py-3 text-left font-semibold">Motivo</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0F3D2E]">
                  {filteredWithdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id} className="hover:bg-[#1A1A1A] transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-[#D4AF37]" />
                          <span className="font-medium">{withdrawal.userName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#BDBDBD]">{withdrawal.userEmail}</td>
                      <td className="px-4 py-4">
                        <span className="font-bold text-[#D4AF37]">
                          ${withdrawal.amount.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <CreditCard size={14} className="text-[#BDBDBD]" />
                          <span className="font-mono text-xs">{withdrawal.clabe}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#BDBDBD]">{withdrawal.beneficiaryName}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-[#BDBDBD]">
                          <Calendar size={14} />
                          <span className="text-xs">{formatDate(withdrawal.createdAt)}</span>
                        </div>
                      </td>
                      {activeTab === 'pending' && (
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(withdrawal)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-lg transition-colors text-xs font-medium"
                            >
                              <CheckCircle size={14} />
                              Aprobar
                            </button>
                            <button
                              onClick={() => handleReject(withdrawal)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#B11217] hover:bg-[#8B0E13] text-white rounded-lg transition-colors text-xs font-medium"
                            >
                              <XCircle size={14} />
                              Rechazar
                            </button>
                          </div>
                        </td>
                      )}
                      {activeTab === 'rejected' && (
                        <td className="px-4 py-4">
                          <span className="text-xs text-[#EF4444]">
                            {withdrawal.rejectionReason || 'Sin motivo especificado'}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
